import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowLeft, MessageSquare, Award, FileText, Image as ImageIcon, Download } from 'lucide-react';
import { toast } from 'sonner';
import 'react-quill-new/dist/quill.snow.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Import ReactQuill dynamically
let ReactQuill = null;
if (typeof window !== 'undefined') {
  ReactQuill = require('react-quill-new').default;
}

// Rich text viewer component  
const RichTextViewer = ({ content }) => {
  if (!content) return <p className="text-slate-500">No answer provided</p>;
  return <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: content }} />;
};

export default function InstructorSubmissions() {
  const { assignmentId } = useParams();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [feedback, setFeedback] = useState({
    answer_comments: [],
    overall_feedback: '',
    grade: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubmissions();
  }, [assignmentId]);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/instructor/submissions/${assignmentId}`);
      setSubmissions(response.data);
    } catch (error) {
      toast.error('Failed to load submissions');
      navigate('/instructor');
    } finally {
      setLoading(false);
    }
  };

  const selectSubmission = (submission) => {
    setSelectedSubmission(submission);
    
    if (submission.feedback) {
      setFeedback(submission.feedback);
    } else {
      const comments = submission.answers.map((ans, idx) => ({
        question_number: idx + 1,
        comment: ''
      }));
      setFeedback({
        answer_comments: comments,
        overall_feedback: '',
        grade: ''
      });
    }
  };

  const handleCommentChange = (index, value) => {
    const newComments = [...feedback.answer_comments];
    newComments[index] = { ...newComments[index], comment: value };
    setFeedback({ ...feedback, answer_comments: newComments });
  };

  const handleSubmitFeedback = async () => {
    try {
      await axios.post(`${API}/instructor/feedback`, {
        submission_id: selectedSubmission.id,
        answer_comments: feedback.answer_comments,
        overall_feedback: feedback.overall_feedback,
        grade: feedback.grade
      });
      
      toast.success('Feedback submitted successfully');
      fetchSubmissions();
      setSelectedSubmission(null);
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="fixed top-0 w-full z-50 glassmorphism">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl font-serif font-bold tracking-tight text-slate-900">
              The Case Verse - Instructor
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/instructor" className="text-slate-700 hover:text-slate-900">Dashboard</Link>
              <Link to="/book-training" className="text-slate-700 hover:text-slate-900">Book Training</Link>
              <Link to="/request-case" className="text-slate-700 hover:text-slate-900">Request Case</Link>
              <button onClick={logout} className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <Link to="/instructor" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4">
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </Link>
            <h1 className="text-4xl font-serif font-bold tracking-tight text-slate-900">
              Student Submissions
            </h1>
          </div>

          {!selectedSubmission ? (
            <div className="space-y-4" data-testid="submissions-list">
              {submissions.length === 0 ? (
                <div className="text-center py-12 bg-white rounded border border-slate-200">
                  <p className="text-slate-600">No submissions yet</p>
                </div>
              ) : (
                submissions.map((submission) => (
                  <div
                    key={submission.id}
                    className="bg-white p-6 rounded border border-slate-200 hover:border-slate-900 transition-all cursor-pointer"
                    onClick={() => selectSubmission(submission)}
                    data-testid={`submission-${submission.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-semibold mb-2">{submission.user_name}</h3>
                        <p className="text-sm text-slate-600 mb-2">{submission.user_email}</p>
                        <p className="text-sm text-slate-500">
                          Submitted: {new Date(submission.submitted_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        {submission.has_feedback ? (
                          <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded">
                            <MessageSquare className="w-4 h-4" />
                            Reviewed
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded">
                            Pending Review
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-8" data-testid="feedback-interface">
              <div className="col-span-2 space-y-6">
                <div className="bg-white p-6 rounded border border-slate-200">
                  <h2 className="text-2xl font-serif font-bold mb-4">Student Answers</h2>
                  <div className="space-y-6">
                    {selectedSubmission.answers.map((answer, index) => (
                      <div key={index} className="border-b border-slate-200 pb-6 last:border-0">
                        <h3 className="font-semibold text-slate-900 mb-2">
                          Question {index + 1}: {answer.question}
                        </h3>
                        <div className="bg-slate-50 p-4 rounded mb-4">
                          <div className="ql-editor" dangerouslySetInnerHTML={{ __html: answer.answer }} />
                          
                          {answer.attachments && answer.attachments.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-slate-200">
                              <div className="font-semibold text-sm mb-2">Attachments:</div>
                              <div className="flex flex-wrap gap-2">
                                {answer.attachments.map((att, attIdx) => (
                                  <a
                                    key={attIdx}
                                    href={att.data}
                                    download={att.name}
                                    className="flex items-center gap-2 px-3 py-2 bg-white rounded border border-slate-200 hover:border-slate-900 transition-all"
                                  >
                                    {att.type.startsWith('image/') ? (
                                      <ImageIcon className="w-4 h-4 text-slate-600" />
                                    ) : (
                                      <FileText className="w-4 h-4 text-slate-600" />
                                    )}
                                    <span className="text-sm">{att.name}</span>
                                    <Download className="w-3 h-3 text-slate-400" />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Your Comment
                          </label>
                          <textarea
                            value={feedback.answer_comments[index]?.comment || ''}
                            onChange={(e) => handleCommentChange(index, e.target.value)}
                            className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                            rows="3"
                            placeholder="Provide feedback on this answer..."
                            data-testid={`comment-${index}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="col-span-1">
                <div className="bg-white p-6 rounded border border-slate-200 sticky top-24">
                  <h2 className="text-xl font-serif font-bold mb-4">Overall Feedback</h2>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Grade
                    </label>
                    <div className="flex items-center gap-2">
                      <Award className="w-5 h-5 text-orange-600" />
                      <input
                        type="text"
                        value={feedback.grade}
                        onChange={(e) => setFeedback({ ...feedback, grade: e.target.value })}
                        className="flex-1 px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                        placeholder="e.g., A, B+, 85/100"
                        data-testid="grade-input"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      General Feedback
                    </label>
                    <textarea
                      value={feedback.overall_feedback}
                      onChange={(e) => setFeedback({ ...feedback, overall_feedback: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                      rows="6"
                      placeholder="Overall comments on the submission..."
                      data-testid="overall-feedback-input"
                    />
                  </div>

                  <div className="space-y-3">
                    <button
                      onClick={handleSubmitFeedback}
                      className="w-full py-3 bg-orange-600 text-white rounded hover:bg-orange-700 transition-all font-medium"
                      data-testid="submit-feedback-button"
                    >
                      Submit Feedback
                    </button>
                    <button
                      onClick={() => setSelectedSubmission(null)}
                      className="w-full py-3 border border-slate-300 rounded hover:border-slate-900 transition-all"
                      data-testid="cancel-button"
                    >
                      Back to List
                    </button>
                  </div>

                  <div className="mt-6 p-4 bg-slate-50 rounded">
                    <p className="text-xs text-slate-600">
                      <strong>Student:</strong> {selectedSubmission.user_name}
                      <br />
                      <strong>Submitted:</strong> {new Date(selectedSubmission.submitted_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
